import React, { createContext, useContext } from 'react';
import type { EmployerSiteName } from 'src/types';
import { Environment } from '../utils/Environment';
import {
  AdvertiserNation,
  getAdvertiserNationFromSite,
} from '../utils/AdvertiserNation';
import {
  getLanguageFromLocale,
  type Language,
  type Locale,
} from '@seek/melways-sites';

interface AppContextContents {
  locale: Locale;
  language: Language;
  site: EmployerSiteName;
  environment: Environment;
  advertiserNation: AdvertiserNation;
  buildVersion: string;
  branchName: string;
}

const defaultContents = {
  locale: 'en-AU' as Locale,
  language: 'en' as Language,
  site: 'employer-seek-au' as EmployerSiteName,
  environment: Environment.production,
  advertiserNation: AdvertiserNation.AU,
  buildVersion: '',
  branchName: '',
};

const AppContext = createContext<AppContextContents>(defaultContents);

type AppContextProviderProps = {
  locale: Locale;
  site: EmployerSiteName;
  environment: Environment;
  buildVersion: string;
  branchName: string;
  children?: React.ReactNode;
};

const AppContextProvider: React.FC<
  React.PropsWithChildren<AppContextProviderProps>
> = ({ locale, site, environment, buildVersion, branchName, children }) => {
  const advertiserNation = getAdvertiserNationFromSite(site);
  const language = getLanguageFromLocale(locale);

  return (
    <AppContext.Provider
      value={{
        locale,
        language,
        site,
        environment,
        advertiserNation,
        buildVersion,
        branchName,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const useAppContext = () => useContext(AppContext);

export { AppContextProvider, useAppContext };
